import { Box, Button, Step, StepLabel, Stepper } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import StoneXRow from '../../StoneX/StoneXRow/StoneXRow';
import StoneXTab from '../../StoneX/StoneXTabs/StoneXTab';
import StoneXTabs from '../../StoneX/StoneXTabs/StoneXTabs';
import { StoneXButton } from '../StoneXButton/StoneXButton';
import StoneXModal from '../StoneXModal/StoneXModal';

export interface StoneXModalSection {
  label: string;
  content: ReactNode;
  valid?: boolean;
}

interface StoneXSectionedModalProps {
  header: string;
  open: boolean;
  onClose: () => void;
  sections: StoneXModalSection[];
  // 'steps' walks through the sections with Back / Next and saves on the last one (for creating);
  // 'tabs' lets the user jump between sections and save from any of them (for editing).
  mode: 'steps' | 'tabs';
  onSave: () => void;
  isSaving?: boolean;
  saveLabel?: string;
  width?: string | number;
  contentMinHeight?: number;
}

export default function StoneXSectionedModal(props: StoneXSectionedModalProps) {
  const {
    header, open, onClose, sections, mode, onSave, isSaving = false, saveLabel = 'Save', width, contentMinHeight = 280,
  } = props;

  const [sectionIndex, setSectionIndex] = useState<number>(0);

  useEffect(() => {
    if (open) {
      setSectionIndex(0);
    }
  }, [open]);

  const isValid = (section: StoneXModalSection) => section.valid !== false;
  const canSave = sections.every(isValid) && !isSaving;
  const isLastSection = sectionIndex === sections.length - 1;
  const saveButton = <StoneXButton onClick={onSave} isWorking={isSaving} disabled={!canSave}>{saveLabel}</StoneXButton>;

  return (
    <StoneXModal header={header} open={open} onClose={onClose} width={width}>
      {mode === 'tabs' ? (
        <StoneXTabs activeTabIndex={sectionIndex}>
          {sections.map((section, index) => (
            <StoneXTab
              key={section.label}
              tabIndex={index}
              isActive={sectionIndex === index}
              onClick={setSectionIndex}
              label={isValid(section) ? section.label : <span style={{ color: 'var(--red-number)' }}>{section.label} *</span>}
            />
          ))}
        </StoneXTabs>
      ) : (
        <Stepper activeStep={sectionIndex} alternativeLabel sx={{ marginBottom: 2 }}>
          {sections.map((section, index) => (
            <Step key={section.label} completed={sectionIndex > index}>
              <StepLabel>{section.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box sx={{ minHeight: contentMinHeight, paddingTop: mode === 'tabs' ? 1 : 0 }}>
        {sections[sectionIndex]?.content}
      </Box>

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        {mode === 'tabs' ? saveButton : (
          <>
            {sectionIndex > 0 && (
              <Button variant="outlined" onClick={() => setSectionIndex(sectionIndex - 1)}>Back</Button>
            )}
            {isLastSection ? saveButton : (
              <StoneXButton onClick={() => setSectionIndex(sectionIndex + 1)} disabled={!isValid(sections[sectionIndex])}>Next</StoneXButton>
            )}
          </>
        )}
      </StoneXRow>
    </StoneXModal>
  );
}
